import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Server,
  Cpu,
  Zap,
  Droplets,
  Shield,
  Leaf,
  Globe,
  Building2,
  ArrowRight,
  CheckCircle2,
  BarChart3,
  Clock,
  Users,
  Layers,
  Lock,
  Activity,
  MapPin,
  TrendingUp,
  HardDrive,
  Fan,
  Network,
  Factory,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

// ---------------------------------------------------------------------------
// DATA
// ---------------------------------------------------------------------------

const stages = [
  {
    id: "hyperscale",
    num: "01",
    label: "Hyperscale Campus",
    sub: "Purpose-built for the largest workloads on earth.",
    Icon: Server,
    color: "from-blue-600 to-cyan-500",
    accent: "blue",
    accentHex: "#2563EB",
    lightBg: "bg-blue-50",
    lightBorder: "border-blue-200",
    dotColor: "bg-blue-600",
    summary:
      "Rapid-deployment campuses engineered for hyperscale cloud and AI workloads. Development-ready sites with comprehensive power enablement, strategic utility partnerships, and engineering designed for adaptive scaling without costly retrofits. From entitlement through energization in a single integrated delivery.",
    bullets: [
      "Multiple gigawatts of secured power across strategic markets",
      "Pre-entitled sites with utility partnerships already in place",
      "Build-to-scale delivery — expand without re-engineering",
      "Single-point accountability from site to commissioning",
      "90-day accelerated design-to-deployment on powered sites",
    ],
  },
  {
    id: "gigascale",
    num: "02",
    label: "Gigascale AI Campus",
    sub: "Liquid-cooled infrastructure for the AI era.",
    Icon: Cpu,
    color: "from-violet-600 to-purple-500",
    accent: "violet",
    accentHex: "#7C3AED",
    lightBg: "bg-violet-50",
    lightBorder: "border-violet-200",
    dotColor: "bg-violet-600",
    summary:
      "Purpose-engineered for ultra-high-density AI and ML training clusters. Delta Flow liquid cooling eliminates the thermal ceiling that stops legacy data centers from supporting next-generation GPU deployments. These are not retrofitted facilities — they are designed from the ground up for 50+ kW per rack.",
    bullets: [
      "Delta Flow direct-to-chip liquid cooling — no thermal ceiling",
      "50+ kW per rack densities supported natively",
      "Designed for NVIDIA GB200, custom ASICs, and next-gen silicon",
      "Closed-loop water systems — 85% reduction in water consumption",
      "AI-specific power distribution and redundancy architecture",
      "Project Caprock: 540 MW sustainable AI campus in NW Texas",
    ],
  },
  {
    id: "enterprise",
    num: "03",
    label: "Multi-Tenant Enterprise",
    sub: "White-glove colocation for mission-critical workloads.",
    Icon: Building2,
    color: "from-emerald-600 to-teal-500",
    accent: "emerald",
    accentHex: "#059669",
    lightBg: "bg-emerald-50",
    lightBorder: "border-emerald-200",
    dotColor: "bg-emerald-600",
    summary:
      "Enterprise-grade colocation with the operational rigor and concierge service that regulated industries demand. Full compliance frameworks, dedicated support engineering, and adaptive infrastructure that scales with your business without forklift upgrades or costly migrations.",
    bullets: [
      "SOC 2 Type II, ISO 27001, HIPAA, PCI-DSS compliance",
      "Dedicated customer success engineering team",
      "Cross-connect fabric to all major cloud on-ramps",
      "N+1 through 2N redundancy configurations",
      "Real-time capacity dashboards and SLA reporting",
    ],
  },
  {
    id: "turnkey",
    num: "04",
    label: "Turnkey Data Halls",
    sub: "Flexible whitespace. Your specs. Our engineering.",
    Icon: Layers,
    color: "from-amber-500 to-orange-500",
    accent: "amber",
    accentHex: "#D97706",
    lightBg: "bg-amber-50",
    lightBorder: "border-amber-200",
    dotColor: "bg-amber-500",
    summary:
      "Pre-engineered data halls that match your exact IT requirements without the timeline or cost of a custom build. Aligned's adaptive infrastructure means the mechanical and electrical systems flex to your workload — not the other way around. Move in ready, with the ability to densify in place.",
    bullets: [
      "Configurable power density from 8 kW to 50+ kW per rack",
      "Adaptive cooling — air, liquid, or hybrid within the same hall",
      "Pre-provisioned fiber, power, and cooling infrastructure",
      "Occupancy in weeks, not quarters",
      "In-place densification without facility migration",
    ],
  },
];

const platformPillars = [
  {
    Icon: Droplets,
    color: "from-cyan-500 to-blue-600",
    title: "Delta Flow Cooling",
    body: "Patented liquid cooling technology that eliminates the thermal ceiling. Direct-to-chip cooling supports 50+ kW racks while reducing water consumption by 85% compared to traditional evaporative systems.",
  },
  {
    Icon: Zap,
    color: "from-amber-500 to-orange-500",
    title: "5 GW Power Pipeline",
    body: "Multiple gigawatts of secured power capacity across strategic North American and Latin American markets. Utility partnerships and grid interconnection rights already in place — not speculative.",
  },
  {
    Icon: Leaf,
    color: "from-emerald-500 to-green-500",
    title: "100% Renewable Match",
    body: "Every megawatt-hour consumed in North America is matched with renewable energy. Not offsets. Not credits purchased after the fact. Matched generation, verified annually.",
  },
  {
    Icon: Shield,
    color: "from-red-500 to-rose-500",
    title: "Adaptive Infrastructure",
    body: "Mechanical and electrical systems that reconfigure without downtime. As workloads evolve from air-cooled enterprise to liquid-cooled AI training, the facility adapts in place — no migration required.",
  },
  {
    Icon: Clock,
    color: "from-violet-500 to-purple-500",
    title: "Speed to Market",
    body: "Pre-entitled, development-ready sites with power and fiber already secured. Move from contract to commissioning faster than any ground-up build — because the hardest problems are already solved.",
  },
  {
    Icon: Lock,
    color: "from-slate-500 to-zinc-600",
    title: "Enterprise Security",
    body: "Physical and logical security architecture designed for the most sensitive workloads. Multi-layer access control, 24/7 NOC, AI-powered threat monitoring, and compliance frameworks for every major regulatory regime.",
  },
];

const campusLocations = [
  { label: "Northern Virginia", region: "NA", flagship: true, mw: "1,200+" },
  { label: "Phoenix, AZ",       region: "NA", flagship: true, mw: "800+" },
  { label: "Dallas, TX",        region: "NA", flagship: true, mw: "600+" },
  { label: "Salt Lake City, UT",region: "NA", flagship: false, mw: "400+" },
  { label: "Chicago, IL",       region: "NA", flagship: false, mw: "300+" },
  { label: "Atlanta, GA",       region: "NA", flagship: false, mw: "200+" },
  { label: "Hillsboro, OR",     region: "NA", flagship: false, mw: "200+" },
  { label: "Maryland",          region: "NA", flagship: false, mw: "300+" },
  { label: "Ohio",              region: "NA", flagship: false, mw: "200+" },
  { label: "NW Texas (Caprock)",region: "NA", flagship: true, mw: "540" },
];

const latamLocations = [
  { label: "Brazil",   region: "LATAM" },
  { label: "Chile",    region: "LATAM" },
  { label: "Colombia", region: "LATAM" },
  { label: "Mexico",   region: "LATAM" },
];

const customerProfiles = [
  {
    Icon: Cpu,
    color: "text-violet-600",
    bg: "bg-violet-50",
    iconBorder: "border-violet-200",
    label: "Hyperscale Cloud",
    blurb: "Multi-hundred megawatt deployments with guaranteed power, cooling, and fiber. One partner, one contract, continent-wide capacity.",
  },
  {
    Icon: Activity,
    color: "text-blue-600",
    bg: "bg-blue-50",
    iconBorder: "border-blue-200",
    label: "AI / ML Platforms",
    blurb: "Liquid-cooled, high-density infrastructure purpose-built for GPU training clusters. No thermal throttling. No power ceiling.",
  },
  {
    Icon: Factory,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    iconBorder: "border-emerald-200",
    label: "Enterprise IT",
    blurb: "Regulated, compliant, white-glove colocation with adaptive capacity. Scale without migrating. Densify without rebuilding.",
  },
  {
    Icon: Network,
    color: "text-amber-600",
    bg: "bg-amber-50",
    iconBorder: "border-amber-200",
    label: "Network & CDN",
    blurb: "Strategic edge presence across ten metros with dense cross-connect fabrics and direct cloud on-ramps to every major provider.",
  },
  {
    Icon: TrendingUp,
    color: "text-rose-600",
    bg: "bg-rose-50",
    iconBorder: "border-rose-200",
    label: "Financial Services",
    blurb: "Ultra-low latency connectivity, SOC 2 / PCI-DSS compliance, and disaster recovery architectures across geographically diverse markets.",
  },
  {
    Icon: Globe,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
    iconBorder: "border-indigo-200",
    label: "Sovereign & Government",
    blurb: "US-based infrastructure with FedRAMP-aligned security frameworks, ITAR support, and dedicated compliance engineering for government workloads.",
  },
];

const byTheNumbers = [
  { value: "85", label: "Data Centers", sub: "Under management or development" },
  { value: "5 GW", label: "Power Pipeline", sub: "Operational and planned capacity" },
  { value: "50", label: "Campuses", sub: "Across the Americas" },
  { value: "100%", label: "Renewable Match", sub: "North American operations" },
  { value: "$2.58B", label: "Credit Facility", sub: "Secured March 2026" },
  { value: "540 MW", label: "Project Caprock", sub: "NW Texas sustainable AI campus" },
];

// ---------------------------------------------------------------------------
// SUB-COMPONENTS
// ---------------------------------------------------------------------------

function SectionLabel({ index, children }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 mb-2">
      {String(index).padStart(2, "0")} / {children}
    </p>
  );
}

function StageButton({ stage, active, onClick, showConnector }) {
  const { Icon } = stage;
  const isActive = active.id === stage.id;

  return (
    <>
      <button
        onClick={() => onClick(stage)}
        className={`w-full rounded-[28px] border p-5 text-left transition-all duration-200 ${
          isActive
            ? "border-zinc-900 bg-zinc-900 text-white shadow-2xl dark:border-white dark:bg-white dark:text-zinc-900"
            : "border-zinc-200 bg-white/80 hover:border-zinc-300 hover:bg-white dark:border-zinc-800 dark:bg-zinc-900/70 dark:hover:border-zinc-600"
        }`}
      >
        <div className="flex items-start gap-4">
          <div
            className={`shrink-0 rounded-2xl bg-gradient-to-br ${stage.color} p-3 text-white shadow-lg`}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div
                  className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${
                    isActive ? "opacity-50" : "text-zinc-400"
                  }`}
                >
                  {stage.num}
                </div>
                <div className="mt-0.5 text-[17px] font-semibold leading-tight">
                  {stage.label}
                </div>
              </div>
              <ArrowRight
                className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                  isActive ? "translate-x-1 opacity-60" : "opacity-30"
                }`}
              />
            </div>
            <p
              className={`mt-1.5 text-sm leading-relaxed ${
                isActive ? "text-zinc-300 dark:text-zinc-600" : "text-zinc-500"
              }`}
            >
              {stage.sub}
            </p>
          </div>
        </div>
      </button>
      {showConnector && (
        <div className="ml-[30px] h-5 w-px bg-zinc-200 dark:bg-zinc-700" />
      )}
    </>
  );
}

function DetailPanel({ stage }) {
  const { Icon } = stage;
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={stage.id}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.26 }}
        className="h-full"
      >
        <Card className="h-full rounded-[32px] border-0 bg-gradient-to-br from-white to-zinc-50 shadow-xl dark:from-zinc-900 dark:to-zinc-950">
          <CardContent className="p-8 lg:p-10">
            <div className="mb-6 flex items-center gap-4">
              <div
                className={`rounded-3xl bg-gradient-to-br ${stage.color} p-4 text-white shadow-xl`}
              >
                <Icon className="h-8 w-8" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">
                  Solution {stage.num}
                </p>
                <h3 className="mt-1 text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
                  {stage.label}
                </h3>
              </div>
            </div>

            <p className="mb-8 text-sm leading-7 text-zinc-600 dark:text-zinc-300">
              {stage.summary}
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              {stage.bullets.map((item) => (
                <div
                  key={item}
                  className={`flex items-start gap-3 rounded-2xl border ${stage.lightBorder} ${stage.lightBg} p-4 dark:border-zinc-800 dark:bg-zinc-900/50`}
                >
                  <div
                    className={`mt-1 h-2 w-2 shrink-0 rounded-full ${stage.dotColor}`}
                  />
                  <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// MAIN EXPORT
// ---------------------------------------------------------------------------

export default function AlignedSalesWall() {
  const [activeStage, setActiveStage] = useState(stages[0]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 via-white to-zinc-100 text-zinc-900 dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-900 dark:text-zinc-100">
      <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">

        {/* -- HERO ---------------------------------------------------- */}
        <section className="py-16 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-6 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide">
                Adaptive Data Center Infrastructure
              </Badge>
              <h1 className="text-5xl font-bold leading-[1.1] tracking-tight sm:text-6xl lg:text-7xl">
                <span className="whitespace-nowrap">Aligned</span>{" "}
                <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Data Centers</span>
              </h1>
              <p className="mt-2 text-2xl font-semibold text-zinc-400 tracking-tight">
                Scale without compromise.
              </p>
              <p className="mt-6 max-w-xl text-lg leading-8 text-zinc-500 dark:text-zinc-400">
                From multi-tenant enterprise colocation to gigawatt-scale AI
                campuses — adaptive infrastructure engineered to grow with your
                most demanding workloads, across the Americas.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <Card className="rounded-[28px] border-0 bg-zinc-900 text-white shadow-2xl dark:bg-zinc-900">
                <CardContent className="p-8">
                  <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">
                    Why Aligned
                  </p>
                  <p className="mb-4 text-sm leading-7 text-zinc-300">
                    Legacy data centers were designed for yesterday's workloads.
                    AI training clusters, GPU-dense inference, and
                    hyperscale cloud demand infrastructure that adapts —
                    mechanically, electrically, and thermally — without
                    downtime, migration, or compromise.
                  </p>
                  <p className="text-sm leading-7 text-zinc-300">
                    Aligned builds that infrastructure. 85 facilities, 5
                    gigawatts of power, 50 campuses, 100% renewable match.
                    One partner for the entire Americas.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* -- BY THE NUMBERS ----------------------------------------- */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={1}>At Scale</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">
              Infrastructure that speaks in gigawatts.
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {byTheNumbers.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.06 }}
                viewport={{ once: true }}
              >
                <Card className="h-full rounded-[24px] border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                  <CardContent className="p-5 text-center">
                    <div className="text-3xl font-bold bg-gradient-to-br from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                      {stat.value}
                    </div>
                    <div className="mt-1 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                      {stat.label}
                    </div>
                    <div className="mt-1 text-xs text-zinc-400">
                      {stat.sub}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* -- SOLUTIONS / PROGRESSION -------------------------------- */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={2}>Solutions</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">
              Right-sized for every workload.
            </h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Four deployment models, one adaptive platform. Start where you
              are. Scale without re-engineering. Every solution shares the same
              operational rigor, sustainability commitment, and engineering DNA.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
            <div>
              {stages.map((stage, idx) => (
                <StageButton
                  key={stage.id}
                  stage={stage}
                  active={activeStage}
                  onClick={setActiveStage}
                  showConnector={idx < stages.length - 1}
                />
              ))}
            </div>
            <DetailPanel stage={activeStage} />
          </div>
        </section>

        {/* -- PLATFORM DIFFERENTIATORS ------------------------------- */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={3}>Competitive Advantage</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">
              What makes Aligned different.
            </h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Not every data center provider can deliver gigawatt-scale
              AI-ready infrastructure with 100% renewable matching and
              patented cooling technology. These are the engineering
              decisions that separate Aligned.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {platformPillars.map((pillar, i) => {
              const { Icon } = pillar;
              return (
                <motion.div
                  key={pillar.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.06 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full rounded-[28px] border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                    <CardContent className="p-6">
                      <div
                        className={`mb-5 inline-flex rounded-2xl bg-gradient-to-br ${pillar.color} p-3 text-white shadow-lg`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="mb-2 text-base font-bold text-zinc-900 dark:text-zinc-100">
                        {pillar.title}
                      </h3>
                      <p className="text-sm leading-7 text-zinc-500 dark:text-zinc-400">
                        {pillar.body}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* -- CAMPUS FOOTPRINT --------------------------------------- */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={4}>Campus Footprint</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">
              50 campuses. Two continents. One partner.
            </h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Strategic presence in the markets that matter most —
              interconnection-dense metros, renewable energy corridors,
              and emerging AI infrastructure hubs across North and Latin America.
            </p>
          </div>

          {/* NA locations */}
          <div className="mb-6">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">
              North America
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {campusLocations.map((loc, i) => (
                <motion.div
                  key={loc.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: i * 0.04 }}
                  viewport={{ once: true }}
                >
                  <div
                    className={`rounded-2xl border p-4 transition-all ${
                      loc.flagship
                        ? "border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30"
                        : "border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900/70"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className={`h-4 w-4 ${loc.flagship ? "text-blue-600" : "text-zinc-400"}`} />
                      <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                        {loc.label}
                      </span>
                    </div>
                    {loc.mw && (
                      <div className="mt-1 text-xs text-zinc-500">
                        {loc.mw} MW capacity
                      </div>
                    )}
                    {loc.flagship && (
                      <Badge className="mt-2 rounded-full px-2 py-0.5 text-[10px] bg-blue-600 text-white border-0">
                        Flagship
                      </Badge>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* LATAM locations */}
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">
              Latin America
            </p>
            <div className="flex flex-wrap gap-2">
              {latamLocations.map((loc) => (
                <span
                  key={loc.label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-1.5 text-xs font-semibold text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-400"
                >
                  <Globe className="h-3 w-3" />
                  {loc.label}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* -- CUSTOMER PROFILES -------------------------------------- */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={5}>Built For</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">
              One platform. Every workload class.
            </h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Whether you are training the next foundation model, running
              mission-critical financial systems, or deploying sovereign cloud
              infrastructure — Aligned has purpose-built the environment
              your workload demands.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-stretch">
            {/* Dark "AI era" card */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
            >
              <Card className="h-full rounded-[32px] border-0 bg-zinc-900 text-white shadow-2xl">
                <CardContent className="flex h-full flex-col justify-between p-8 lg:p-10">
                  <div>
                    <div className="mb-6 inline-flex rounded-3xl bg-gradient-to-br from-blue-600 to-cyan-500 p-4 text-white shadow-xl">
                      <HardDrive className="h-8 w-8" />
                    </div>
                    <h3 className="mb-4 text-2xl font-bold">
                      The AI Infrastructure Partner
                    </h3>
                    <p className="mb-4 text-sm leading-7 text-zinc-300">
                      The largest AI training runs in the world require
                      infrastructure that most data center providers
                      cannot deliver: liquid cooling at rack scale, power
                      density above 50 kW, and hundreds of megawatts
                      in a single campus — energized, not entitled.
                    </p>
                    <p className="text-sm leading-7 text-zinc-300">
                      Aligned was purpose-built for this moment. Delta
                      Flow cooling, gigawatt-scale power, and adaptive
                      infrastructure mean your deployment grows with
                      your ambition — not against your facility.
                    </p>
                  </div>

                  <div className="mt-8 border-t border-white/10 pt-6">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                      Trusted by
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {["Oracle Cloud Infrastructure", "Lambda", "Nutanix", "Enterprise & Government"].map(
                        (name) => (
                          <span
                            key={name}
                            className="rounded-xl border border-white/10 bg-white/[0.07] px-3 py-1 text-xs text-zinc-300"
                          >
                            {name}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Customer profile grid */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {customerProfiles.map((item, i) => {
                const { Icon } = item;
                return (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.06 }}
                    viewport={{ once: true }}
                  >
                    <Card className="h-full rounded-[28px] border border-zinc-100 bg-white shadow-sm transition-all hover:border-zinc-200 hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900/70">
                      <CardContent className="p-5">
                        <div
                          className={`mb-4 flex h-11 w-11 items-center justify-center rounded-2xl border ${item.iconBorder} ${item.bg} ${item.color}`}
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                        <h4 className="mb-2 text-sm font-bold text-zinc-900 dark:text-zinc-100">
                          {item.label}
                        </h4>
                        <p className="text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                          {item.blurb}
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* -- CLOSING FRAME ------------------------------------------ */}
        <section className="py-10 lg:py-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
          >
            <Card className="rounded-[32px] border-0 bg-gradient-to-r from-blue-900 via-blue-800 to-cyan-900 text-white shadow-2xl">
              <CardContent className="p-8 lg:p-10">
                <p className="text-sm font-medium uppercase tracking-[0.2em] text-blue-300/70">
                  The bottom line
                </p>
                <h2 className="mt-2 text-3xl font-bold tracking-tight">
                  Your workload is evolving. Your infrastructure should too.
                </h2>
                <p className="mt-4 max-w-3xl text-base leading-7 text-blue-100/80">
                  Aligned Data Centers delivers adaptive, sustainable,
                  enterprise-grade infrastructure at gigawatt scale — from
                  multi-tenant colocation to the largest AI training campuses
                  in the Americas. One partner. Every workload. No compromises.
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  {[
                    "85 Data Centers",
                    "5 GW Power",
                    "50 Campuses",
                    "100% Renewable",
                    "Delta Flow Cooling",
                  ].map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-sm font-medium text-white"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

      </div>
    </div>
  );
}
